/*
 * [APP-FIT 2026-10-26, user] "whatever it is looking right now on my old laptop on 100 it should be same for all the
 * screens on the 100 … I should not be do the 67 to see the proper layout". The app is drawn in fixed px and was designed on
 * the user's old 1920 x 1080 screen; on the 14-inch 1366 x 768 laptop the same px did not fit (2 of the 4 clocks, the In Bank
 * Summary scrolling sideways, a cut table, the login card cut at the bottom). So the WHOLE app is laid out as on a 1920 px
 * wide screen and scaled to the window with CSS zoom on <html> — what Edge's 67 % did by hand, now automatic at 100 %:
 * 1366 → 0.711, 1440 → 0.75, 1536 → 0.8, 1920 → 1 (unchanged), 2560 → 1.333. Lead Management's 2026-10-14 auto-fit was
 * the first page done this way; this replaces it for every page.
 *  - Phones (768 px or less) are never scaled: they keep their own phone layout.
 *  - The TV boards (PM Singapore, Team ROW, Dubai) scale their own stage to the screen, so AppLayout turns this zoom off
 *    while one is open (setAppZoomBoard).
 *  - Units under the zoom: getBoundingClientRect(), clientX / clientY and innerWidth / innerHeight are SCREEN px; top /
 *    left / width / height set on an element, offsetWidth / clientWidth / scrollLeft are PAGE px (screen px / zoom). Code
 *    that places a popup from a screen position converts with pageRect / pageViewport / toPage.
 *  - CSS vh / vw are zoomed like px, so a size given in vh / vw is written as calc(100vh / var(--app-zoom, 1)).
 *  - Media queries see the real window, not the laid-out width: "@media (max-width: 1560px)" would still fire on the 1366
 *    laptop although its page is laid out 1920 px wide. Such laptop rules use layoutMax / layoutMin / layoutBetween, which
 *    follow the LAID-OUT width (window / zoom) through <html data-lw>. Phone rules (768 px) stay plain media queries.
 */
const DESIGN_W = 1920;
const FIT_MIN = 0.6;
const FIT_MAX = 1.5;
const PHONE_MAX = 768;
const CAN_ZOOM = typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('zoom', '2');

let zoom = 1;
let board = false;
let started = false;
let queued = false;
const breaks = new Set<number>();

const fit = (): number => {
  if (!CAN_ZOOM || board || window.innerWidth <= PHONE_MAX) return 1;
  return Math.min(FIT_MAX, Math.max(FIT_MIN, Math.round((window.innerWidth / DESIGN_W) * 1000) / 1000));
};

function apply(): void {
  const next = fit();
  const html = document.documentElement;
  const laidOut = window.innerWidth / next;
  const lw = Array.from(breaks).sort((a, b) => a - b).filter((x) => laidOut <= x).map((x) => `le${x}`).join(' ');
  if (html.getAttribute('data-lw') !== lw) html.setAttribute('data-lw', lw);
  const z = next === 1 ? '' : String(next);
  if (html.style.getPropertyValue('zoom') !== z) {
    if (z) html.style.setProperty('zoom', z);
    else html.style.removeProperty('zoom');
  }
  if (html.style.getPropertyValue('--app-zoom') !== String(next)) html.style.setProperty('--app-zoom', String(next));
  zoom = next;
}

/** Starts the fit (index.tsx, before the first render) and follows the window's size (browser zoom fires resize too). */
export function startAppZoom(): void {
  if (started || typeof window === 'undefined') return;
  started = true;
  apply();
  window.addEventListener('resize', apply);
}

/** AppLayout: a TV board that scales its own stage is open (true) or closed (false). */
export function setAppZoomBoard(on: boolean): void {
  if (board === on) return;
  board = on;
  if (started) apply();
}

export const getAppZoom = (): number => zoom;

/** Screen px → page px. */
export const toPage = (screenPx: number): number => screenPx / zoom;

/** An element's box, or a DOMRect measured earlier, in page px. */
export function pageRect(of: Element | DOMRectReadOnly): { left: number; top: number; right: number; bottom: number; width: number; height: number } {
  const r = of instanceof Element ? of.getBoundingClientRect() : of;
  const z = zoom;
  return { left: r.left / z, top: r.top / z, right: r.right / z, bottom: r.bottom / z, width: r.width / z, height: r.height / z };
}

/** The window in page px. */
export const pageViewport = (): { width: number; height: number } => ({ width: window.innerWidth / zoom, height: window.innerHeight / zoom });

function register(x: number): void {
  if (breaks.has(x)) return;
  breaks.add(x);
  if (started && !queued) {
    queued = true;
    queueMicrotask(() => {
      queued = false;
      apply();
    });
  }
}

/** A laptop rule for "the page is laid out X px wide or less" — was @media (max-width: Xpx). Use: ${layoutMax(1560)} { … } */
export function layoutMax(x: number): string {
  register(x);
  return `:where(html[data-lw~="le${x}"]) &`;
}

/** A laptop rule for "laid out Y px wide or more" — was @media (min-width: Ypx). */
export function layoutMin(y: number): string {
  register(y - 1);
  return `:where(html:not([data-lw~="le${y - 1}"])) &`;
}

/** Both at once — was @media (min-width: MINpx) and (max-width: MAXpx). */
export function layoutBetween(min: number, max: number): string {
  register(min - 1);
  register(max);
  return `:where(html[data-lw~="le${max}"]:not([data-lw~="le${min - 1}"])) &`;
}
